// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type { CoADTO, CoACreateRequest } from '../types/coa';
import type { PageResponse } from '../types/relationship';

/**
 * Phase 6 advisor-side client for the Change of Agency REST endpoints
 * exposed by the resource-server (Phase 3 / Phase 4 controllers).
 *
 * <p>Mirrors the shape of {@link loaApi} so the CoA wizard composes the
 * same way the relationship wizard composes against {@code loaApi}.
 */
export const coaApi = {
  /**
   * List CoAs for a profile (paginated). Sorted createdAt DESC server-side.
   */
  getCoAsByProfile: async (
    profileId: string,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<CoADTO>> => {
    const response = await api.get<PageResponse<CoADTO>>(`/coa/profile/${profileId}`, {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Fetch a single CoA by id.
   */
  getCoA: async (id: string): Promise<CoADTO> => {
    const response = await api.get<CoADTO>(`/coa/${id}`);
    return response.data;
  },

  /**
   * Create a new Change of Agency. Server resolves the effective agency
   * code per policy at creation time and dispatches the client invitation
   * email asynchronously (Phase 4).
   */
  createCoA: async (data: CoACreateRequest): Promise<CoADTO> => {
    const response = await api.post<CoADTO>('/coa', data);
    return response.data;
  },

  /**
   * Transition a DRAFT/GENERATING/FAILED CoA to PENDING_SIGNATURE.
   */
  sendForSigning: async (id: string): Promise<CoADTO> => {
    const response = await api.post<CoADTO>(`/coa/${id}/send-for-signing`);
    return response.data;
  },

  /**
   * Download the signed CoA PDF as a blob (advisor download flow).
   */
  downloadSignedDocument: async (id: string): Promise<Blob> => {
    const response = await api.get(`/coa/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Void / cancel a non-terminal CoA.
   */
  voidCoA: async (id: string, reason: string): Promise<CoADTO> => {
    const response = await api.post<CoADTO>(`/coa/${id}/void`, { reason });
    return response.data;
  },

  /**
   * Trigger a per-pending-policy nudge to every provider whose CoA policy
   * row is still PENDING. Returns the number of nudges dispatched.
   */
  nudgeProvider: async (id: string): Promise<{ coaId: string; status: string; dispatchedCount: number; message: string }> => {
    const response = await api.post(`/coa/${id}/nudge-provider`);
    return response.data;
  },
};
